import {useEffect, useState} from 'react';
import {usePermissions, useGetList} from "react-admin";
import {axiosApiInstance} from "../AxiosUtils";
import hasPermission from "../auth/hasPermission";
import {useScope} from "../scope/ScopeContext";

export const useCharityChoices = () => {
    const [charityChoices, setCharityChoices] = useState([]);

    // We need to check the  permissions of the users
    const {permissions} = usePermissions();

    // Respect the workspace scope: when an admin has selected an umbrella, narrow
    // the charity options to that umbrella. umbrella_id is an admin-only concept —
    // for umbrella/charity roles the role-specific backend endpoint already scopes
    // by identity, so we never forward a (possibly stale) umbrella_id for them.
    // v2 charities listing takes umbrella_id as the umbrella's UUID (external id),
    // not its numeric id — use the scope's umbrellaExternalId accordingly.
    const {umbrellaExternalId} = useScope();
    const isAdmin = permissions === 'admin';
    const scopedUmbrellaExternalId = isAdmin ? umbrellaExternalId : null;

    const {data: charities} = useGetList('charities', {
        pagination: { page: 1, perPage: 50 },
        sort: {field: 'charityName', order: 'ASC'},
        filter: scopedUmbrellaExternalId ? {umbrella_id: scopedUmbrellaExternalId} : {},
    });

    useEffect(() => {
        if (charities && hasPermission(permissions, ["list"], "charityChoices") ) {
            const myCharityChoices = charities.map(charity => ({
                id: charity.id,
                external_id: charity.external_id,
                name: charity.charity_name,
                active: charity.active,
                allow_text_messages: charity.allow_text_messages,
                allow_photo_messages: charity.allow_photo_messages,
                allow_video_messages: charity.allow_video_messages
            }));
            setCharityChoices(myCharityChoices);
        }
    }, [charities, permissions]);

    return charityChoices;
};

export const useUmbrellaChoices = () => {
    const [umbrellaChoices, setUmbrellaChoices] = useState([]);

    // We need to check the permissions of the users
    const {permissions} = usePermissions();

    useEffect(() => {
        if ( hasPermission(permissions, ["list"], "umbrella") ) {
            const fetchUmbrellas = async () => {
                // Only admin users need the umbrella filter
                    await axiosApiInstance.get(`${process.env.REACT_APP_API_URL}/api/v1/admin/umbrellas?size=50`).then((response) => {
                        const umbrellas = response.data._embedded.charities;
                        let myCharityChoices = [];
                        for (let i = 0; i < umbrellas.length; i++) {
                            let umbrella = umbrellas[i];
                            let jsonOjbect = {name: umbrella.charity_name, id: umbrella.id, active: umbrella.active, external_id: umbrella.external_id}
                            myCharityChoices.push(jsonOjbect);
                        }
                        setUmbrellaChoices(myCharityChoices);
                    });
            };

            fetchUmbrellas().then(r => {
            });
        }
    }, [permissions]);

    return umbrellaChoices;
};

// v2 umbrella choices (UUID-keyed), for v2-flow consumers only — the provider's v2
// umbrellas GET_LIST routes these roles to the v2 endpoint, so `u.id` is the UUID
// (external id). `useUmbrellaChoices` above stays untouched (v1 numeric) for its
// own v1-flow consumers.
export const useUmbrellaUuidChoices = () => {
    const {permissions} = usePermissions();
    const {data} = useGetList('umbrellas', {
        pagination: {page: 1, perPage: 100},
        sort: {field: 'charity_name', order: 'ASC'},
    }, {enabled: permissions === 'admin' || permissions === 'umbrella'});
    return (data || []).map((u) => ({id: u.id, name: u.charity_name, active: u.active}));
};

// v1 numeric charity choices, axios-based (role-aware URL, no v2 dataProvider
// routing) — for screens still on the v1 write flow (volunteers, posts). Mirrors
// filter/useUmbrellasAndCharities.js's charities fetch. `umbrellaId`, when passed,
// is the umbrella's NUMERIC v1 id (admin-only concept, same as that hook).
export const useCharityChoicesNumericV1 = (umbrellaId = null) => {
    const [charityChoices, setCharityChoices] = useState([]);
    const {permissions} = usePermissions();

    const rolePath = permissions === 'admin' ? 'admin' : permissions === 'umbrella' ? 'umbrellad' : 'charitad';
    const urlPrefix = `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}`;

    useEffect(() => {
        const fetchCharities = async () => {
            if (!hasPermission(permissions, ["list"], "charityChoices")) return;
            const umbrellaParam = umbrellaId ? `&umbrella_id=${umbrellaId}` : '';
            await axiosApiInstance.get(`${urlPrefix}/charities?size=50&sort=charityName,ASC${umbrellaParam}`)
                .then((response) => {
                    const charities = response.data._embedded?.charities || [];
                    // Include the allow_*_messages flags alongside the {id, name, active}
                    // core mapping — PostEditStatamic derives its message-type choices from
                    // these on the returned charity choice, same fields v1's numeric response
                    // already carries at no extra request cost.
                    setCharityChoices(charities.map((charity) => ({
                        id: charity.id,
                        external_id: charity.external_id,
                        name: charity.charity_name,
                        active: charity.active,
                        allow_text_messages: charity.allow_text_messages,
                        allow_photo_messages: charity.allow_photo_messages,
                        allow_video_messages: charity.allow_video_messages,
                    })));
                })
                .catch((error) => {
                    console.error("Error fetching charities:", error);
                    setCharityChoices([]);
                });
        };

        fetchCharities();
    }, [permissions, urlPrefix, umbrellaId]);

    return charityChoices;
};

// GET /api/v2/admin/users — admin-only picker (RNAction user_ids reference). Same
// {id, given_name, family_name} row shape as v1 (id is the Keycloak string, unchanged
// both sides), so the {id, name} mapping below is untouched — only the URL moved.
export const useFetchUsers = () => {
    const [userChoices, setUserChoices] = useState([]);
    const { permissions } = usePermissions();

    useEffect(() => {
        const fetchUsers = async () => {
            if (hasPermission(permissions, ["list"], "users")) {
                try {
                    const response = await axiosApiInstance.get(
                        `${process.env.REACT_APP_API_URL}/api/v2/admin/users`,
                        {
                            params: { size: 1000, sort: 'givenName,ASC' }, // Fetch a larger batch
                        }
                    );

                    const users = response.data._embedded?.users?.map(user => ({
                        id: user.id,
                        name: `${user.given_name} ${user.family_name}`,
                    })) || [];
                    setUserChoices(users);
                } catch (error) {
                    console.error("Error fetching users:", error);
                    setUserChoices([]);
                }
            }
        };

        fetchUsers();
    }, [permissions]);

    return userChoices;
};

export const useMetadataDefinitions = (entityType) => {
    const [metadataDefinitions, setMetadataDefinitions] = useState([]);
    const { permissions } = usePermissions();
    
    useEffect(() => {
        const fetchMetadataDefinitions = async () => {
            if (!entityType) return;
            
            try {
                const role = localStorage.getItem('role');
                const rolePath = role === 'admin' ? 'admin' : role === 'umbrella' ? 'umbrellad' : 'charitad';
                
                const response = await axiosApiInstance.get(
                    `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}/metadata-definitions`,
                    {
                        params: { 
                            entity_type: entityType,
                            active: true,
                            size: 100,
                            sort: 'displayOrder,ASC'
                        }
                    }
                );
                
                const definitions = response.data._embedded?.metadata_definitions || [];
                
                // Filter by entity type AND permissions
                let filteredDefinitions = definitions.filter(def => 
                    def.entity_type === entityType
                );
                
                // Additional filtering based on permissions for charity role
                if (role === 'charity') {
                    filteredDefinitions = filteredDefinitions.filter(def => def.charity_can_view);
                }
                
                setMetadataDefinitions(filteredDefinitions);
            } catch (error) {
                console.error("Error fetching metadata definitions:", error);
                setMetadataDefinitions([]);
            }
        };
        
        fetchMetadataDefinitions();
    }, [entityType, permissions]);
    
    return metadataDefinitions;
};
